import { Alert, Card, Group, Stack, Switch, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPhoto } from '@tabler/icons-react';
import { HOME_BACKGROUND_COPY as C, TV_QUALITY_WARNING_COPY, HOME_IMAGE_BRIGHT_CODE, type TvPlatformKey } from '@/lib/constants/appFactory';
import type { AppCfgsDto } from '@/lib/types/App';
import type { FactoryBrandKit } from '@/lib/types/AppFactory';
import { useResellerBrand } from '@/shared/hooks/useResellerBrand';
import { AppPreviewUtils } from '../AppPreviewUtils';
import { HomeBackgroundUtils } from '../HomeBackgroundUtils';
import { useSetHomeBackground } from '../useAppMutations';
import { useAppPreviewBrowse, useAppPreviewCfg } from '../useAppPreview';
import { AppTvFrame } from './AppTvFrame';

interface AppHomeBackgroundProps {
  appCfgId: number;
  app: AppCfgsDto;
  /** The app's newest Brand Kit revision; null = none. */
  kit: FactoryBrandKit | null;
  /** Drafts from the Brand Kit wait for approval. */
  hasDrafts: boolean;
  /** The TV platforms that are on (the preview asks for the first one's payload). */
  tv: readonly TvPlatformKey[];
  /** The kit's quality report says the background image is bright (`home_image_bright`): the text over it may be hard to read. */
  bright?: boolean;
}

const PREVIEW_MAX_WIDTH = 480;

/**
 * The home background switch (3.44.0; design ADR-0016 D5): whether the app's
 * background image is the picture of its TV home screen — AS UPLOADED, no
 * darkening, no blur; the featured title's text sits on its left side over the
 * dark gradient and the videos appear in rows below. An app setting, off by
 * default, saved at once — it is not part of a Generate. Beside the switch,
 * the home screen drawn with the image (the waiting draft's when there is
 * one), so the result is seen BEFORE it is turned on; a bright image gets the
 * factory's warning line (the customer carries the result; nothing is gated).
 * Anyone who can edit the app can set it: customer, reseller, operator.
 */
export function AppHomeBackground({ appCfgId, app, kit, hasDrafts, tv, bright = false }: AppHomeBackgroundProps) {
  const isReseller = useResellerBrand();
  const mutation = useSetHomeBackground(appCfgId);
  const view = HomeBackgroundUtils.view({
    core: app.appCfg.core,
    kit,
    hasDrafts,
    lightTheme: HomeBackgroundUtils.isLightTheme(app),
    isReseller,
  });
  // The mutation stays pending until the app is re-read, so its value stands in for the stored one meanwhile.
  const checked = mutation.isPending ? mutation.variables.enabled : view.checked;

  const save = (enabled: boolean) => {
    mutation.mutate(
      { app, enabled },
      {
        onSuccess: (res) => {
          // An api that does not know the setting answers 200 and stores nothing: say so instead of a silent flip back.
          const stored = res?.appCfgDto?.appCfg?.core;
          if (stored && (stored.homeBgEnabled ?? false) !== enabled) {
            notifications.show({ title: 'Not saved', message: C.notAvailable, color: 'orange' });
            return;
          }
          // Nothing is live before the approval: say when it will show, not that it shows.
          const on = view.kind === 'draft' ? C.savedOnDraft : C.savedOn;
          notifications.show({ title: 'Saved', message: enabled ? on : C.savedOff, color: 'green' });
        },
        onError: (err) =>
          notifications.show({
            title: 'Error',
            message: err.message?.includes('409') || err.message?.includes('conflict') ? C.conflict : err.message,
            color: 'red',
          }),
      },
    );
  };

  return (
    <Card withBorder radius="md" p="md" data-testid="home-background" data-home-bg-kind={view.kind}>
      <Stack gap="md">
        <Group gap="xs">
          <IconPhoto size={20} />
          <Text fw={600} size="md">
            {C.title}
          </Text>
        </Group>

        <Group align="flex-start" gap="xl" wrap="wrap">
          <Stack gap={6} style={{ flex: '1 1 320px', minWidth: 0 }} maw={520}>
            <Switch
              label={C.switchLabel}
              description={C.switchHelp}
              checked={checked}
              disabled={view.disabled || mutation.isPending}
              onChange={(e) => save(e.currentTarget.checked)}
            />
            {view.note && (
              <Text size="sm" c={view.disabled ? 'dimmed' : undefined} data-testid="home-background-note">
                {view.note}
              </Text>
            )}
            {bright && view.kind !== 'none' && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" py={6} data-testid="home-background-bright">
                <Text size="sm">{TV_QUALITY_WARNING_COPY[HOME_IMAGE_BRIGHT_CODE]}</Text>
              </Alert>
            )}
          </Stack>

          {view.previewUrl && <HomeBackgroundPreview app={app} tv={tv} url={view.previewUrl} isDraft={view.previewIsDraft} />}
        </Group>
      </Stack>
    </Card>
  );
}

interface HomeBackgroundPreviewProps {
  app: AppCfgsDto;
  tv: readonly TvPlatformKey[];
  url: string;
  isDraft: boolean;
}

/**
 * The app's real home screen (its live cfg + browse) with the background
 * image as its picture, switch on or off. An app whose address is not set up
 * yet, or whose payload cannot be read, shows the image on its own.
 */
function HomeBackgroundPreview({ app, tv, url, isDraft }: HomeBackgroundPreviewProps) {
  const appDomain = AppPreviewUtils.appDomain(app);
  const platform = tv[0] ?? 'roku';
  const cfgQuery = useAppPreviewCfg(appDomain, platform);
  const browseQuery = useAppPreviewBrowse(appDomain, platform);
  const cfg = cfgQuery.data;
  const browse = browseQuery.data;

  if (cfg && browse) {
    return (
      <Stack gap={6} style={{ flex: `1 1 ${PREVIEW_MAX_WIDTH}px`, minWidth: 0 }} maw={PREVIEW_MAX_WIDTH} data-testid="home-background-preview">
        <AppTvFrame cfg={cfg} browse={browse} maxWidth={PREVIEW_MAX_WIDTH} backdropUrl={url} />
        <Text size="xs" c="dimmed">
          {isDraft ? C.previewCaptionDraft : C.previewCaption}
        </Text>
      </Stack>
    );
  }
  return (
    <Stack gap={6} style={{ flex: `1 1 ${PREVIEW_MAX_WIDTH}px`, minWidth: 0 }} maw={PREVIEW_MAX_WIDTH} data-testid="home-background-image">
      <div
        style={{
          width: '100%',
          aspectRatio: '16 / 9',
          overflow: 'hidden',
          borderRadius: 6,
          border: '1px solid var(--mantine-color-gray-3)',
          background: 'var(--mantine-color-dark-8)',
        }}
      >
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
      <Text size="xs" c="dimmed">
        {C.imageCaption}
      </Text>
    </Stack>
  );
}
